import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import {
    Badge,
    Box,
    Fade,
    Slide,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

const StyledTabs = styled(Tabs)(({ theme }) => ({
    minHeight: 44,
    "& .MuiButtonBase-root": {
        minWidth: "unset !important",
    },
    "& .MuiTabs-flexContainer": {
        gap: theme.spacing(0.5),
    },
    "& .MuiTabs-scrollButtons": {
        width: 32,
        margin: theme.spacing(0, 0.5),
        transition: "all 0.2s ease",
        color: theme.palette.text.secondary,
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.primary.main,
        },
        "&.Mui-disabled": {
            opacity: 0,
            width: 0,
            margin: 0,
            overflow: "hidden",
        },
    },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontWeight: 500,
    textTransform: "none",
    minWidth: "auto",
    minHeight: 36,
    padding: "8px 14px",
    borderRadius: theme.shape.borderRadius,
    transition: "all 0.2s ease",
    "& .MuiSvgIcon-root": {
        fontSize: "1.05rem",
    },
    "&.Mui-selected": {
        color: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main),
        "& *": {
            color: "inherit",
        },
    },
    "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        color: theme.palette.primary.main,
        "& *": {
            color: "inherit",
        },
    },
    "&.Mui-selected:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.14),
    },
    "&.Mui-disabled": {
        opacity: 0.5,
    },
    "& *": {
        color: "inherit",
    },
}));

const TabPanel = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    minHeight: 200,
}));

/**
 * Nested tab bars share a scroll container, so every sticky header pinning at
 * `top: 0` would stack on top of the one above it. Each level publishes where
 * its own header ends, and how deep it sits, so the level below pins just under
 * it and paints behind it.
 */
const StickyHeaderContext = createContext({ offset: 0, depth: 0 });

const BASE_HEADER_Z_INDEX = 10;

const GenericTabs = ({
    tabs,
    defaultTab,
    orientation = "horizontal",
    variant = "standard",
    centered = false,
    allowScrollButtons = true,
    animation = "fade",
    showBadges = true,

    headerColor,
    contentPadding = 3,
    onTabChange,
    actionButtons = null,
}) => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(
        defaultTab || tabs[0]?.value || "",
    );

    const parentSticky = useContext(StickyHeaderContext);
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const node = headerRef.current;
        if (!node) return undefined;

        const measure = () => setHeaderHeight(node.offsetHeight);
        measure();

        if (typeof ResizeObserver === "undefined") return undefined;
        // The header grows when the scroll buttons appear or the labels wrap.
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const stickyForChildren = useMemo(
        () => ({
            offset: parentSticky.offset + headerHeight,
            depth: parentSticky.depth + 1,
        }),
        [parentSticky.offset, parentSticky.depth, headerHeight],
    );

    useEffect(() => {
        if (defaultTab == null || defaultTab === "") return;
        const isValid = tabs.some((tab) => tab.value === defaultTab);
        if (isValid) {
            setActiveTab(defaultTab);
        }
        // Intentionally depend only on defaultTab so parent can drive selected tab without
        // re-running every render when `tabs` is a new array reference.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultTab]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        onTabChange?.(newValue);
    };

    const activeTabData = tabs.find((tab) => tab.value === activeTab);

    const getTabColor = (color) => {
        // Use neutral colors for minimal design
        return theme.palette.text.primary;
    };

    const renderTabLabel = (tab) => {
        const hasBadge = showBadges && tab.badge && tab.badge > 0;

        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {tab.icon && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {tab.icon}
                    </Box>
                )}
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: "inherit",
                        color: "inherit",
                    }}
                >
                    {tab.label}
                </Typography>
                {hasBadge && (
                    <Badge
                        badgeContent={tab.badge}
                        color="error"
                        sx={{
                            "& .MuiBadge-badge": {
                                fontSize: "0.7rem",
                                height: 18,
                                minWidth: 18,
                            },
                        }}
                    />
                )}
            </Box>
        );
    };

    const renderTabContent = () => {
        if (!activeTabData) return null;

        const content = (
            <TabPanel sx={{ p: contentPadding }}>
                {activeTabData.content}
            </TabPanel>
        );

        if (animation === "slide") {
            return (
                <Slide direction="up" in timeout={300}>
                    {content}
                </Slide>
            );
        }

        return (
            <Fade in timeout={300}>
                {content}
            </Fade>
        );
    };

    return (
        <StickyHeaderContext.Provider value={stickyForChildren}>
            <Box
                sx={{
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                {/* Tabs Header */}
                <Box
                    ref={headerRef}
                    sx={{
                        position: "sticky",
                        top: parentSticky.offset,
                        zIndex: Math.max(
                            1,
                            BASE_HEADER_Z_INDEX - parentSticky.depth,
                        ),
                        marginRight: 2,
                        marginLeft: 2,
                        backgroundColor:
                            headerColor || theme.palette.background.paper,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        pt: 1,
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <StyledTabs
                            value={activeTab}
                            onChange={handleTabChange}
                            orientation={orientation}
                            variant={variant}
                            centered={variant !== "scrollable" && centered}
                            scrollButtons={
                                variant === "scrollable" ? "auto" : false
                            }
                            allowScrollButtonsMobile={
                                variant === "scrollable" && allowScrollButtons
                            }
                            slots={{
                                StartScrollButtonIcon: ChevronLeftIcon,
                                EndScrollButtonIcon: ChevronRightIcon,
                            }}
                        >
                            {tabs.map((tab) => (
                                <StyledTab
                                    key={tab.value}
                                    value={tab.value}
                                    label={renderTabLabel(tab)}
                                    disabled={tab.disabled}
                                    sx={{
                                        color: tab.disabled
                                            ? theme.palette.text.disabled
                                            : undefined,
                                    }}
                                />
                            ))}
                        </StyledTabs>
                    </Box>
                    {actionButtons && (
                        <Box
                            sx={{
                                flexShrink: 0,
                                paddingLeft: 1,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            {actionButtons}
                        </Box>
                    )}
                </Box>

                {/* Tab Content */}
                {renderTabContent()}
            </Box>
        </StickyHeaderContext.Provider>
    );
};

export default GenericTabs;
